/**
 * The half-finished login — "password accepted, waiting for the code" — kept across a page reload.
 *
 * It lived only in React state, so anything that reloaded the tab between the password and the
 * authenticator code dropped the person back at the password screen with no explanation. A dev
 * server deciding it cannot hot-update is one way; a phone reclaiming memory while its owner
 * switches to their authenticator app is the one that will happen in production, and switching
 * apps is a required step of this flow rather than an unusual thing to do.
 *
 * **What is stored is not a credential.** `challengeId` is a random id whose only power is to be
 * offered alongside a valid TOTP code; it sets no cookie, no protected route accepts it, and the
 * server drops it after ten minutes (`auth/totpChallenge.ts`). `sessionStorage` matches that
 * lifetime closely — per tab, gone when the tab closes — and is deliberately not `localStorage`.
 */

const KEY = "sga.preauth";

/** Kept in step with CHALLENGE_TTL_SECONDS on the server. A restore past it would only 401. */
const MAX_AGE_MS = 10 * 60_000;

export type PreAuthStep =
  | { step: "login" }
  | { step: "totp-challenge"; challengeId: string }
  | { step: "totp-enrol"; challengeId: string };

interface Stored {
  step: Exclude<PreAuthStep["step"], "login">;
  challengeId: string;
  at: number;
}

export const readPreAuth = (): PreAuthStep => {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return { step: "login" };

    const stored = JSON.parse(raw) as Stored;
    if (!stored?.challengeId || Date.now() - stored.at > MAX_AGE_MS) {
      window.sessionStorage.removeItem(KEY);
      return { step: "login" };
    }
    return { step: stored.step, challengeId: stored.challengeId };
  } catch {
    // Private browsing, storage disabled, or a value from an older shape. Falling back to the
    // password screen is the correct failure: it costs one re-entry and nothing else.
    return { step: "login" };
  }
};

export const writePreAuth = (value: PreAuthStep): void => {
  try {
    if (value.step === "login") {
      window.sessionStorage.removeItem(KEY);
      return;
    }
    window.sessionStorage.setItem(
      KEY,
      JSON.stringify({ step: value.step, challengeId: value.challengeId, at: Date.now() }),
    );
  } catch {
    // Storage being unavailable must not break signing in — it only means a reload costs a retype.
  }
};

export const clearPreAuth = (): void => writePreAuth({ step: "login" });
